import { AiPlatform, AssistantSettings } from '../script';
import { CHATBOT_AI_ASSISTANT_MODELS } from './ai-assistant.model';

/**
 * Trợ lý AI mới (wizard 4 bước + các tab): hồ sơ, quy tắc, page gắn trợ lý. Khớp BE GenAI\Model\AIAssistant::parseSetting
 * (settings.profile, presets, rules, mode, retrieval) và Chatbot\Service\ChatbotAI\AssistantstudioApiService.
 */
export type AiStudioMode = 'auto' | 'suggest';
export type AiStudioCertainty = 'careful' | 'balanced' | 'flexible';
export type AiStudioLength = 'short' | 'standard' | 'detailed';
export type AiStudioFallbackMode = 'template_handoff' | 'ask_once' | 'general' | 'silent';
export type AiStudioHoursType = 'always' | 'schedule' | 'outside';
export type AiStudioTier = 'economy' | 'balanced' | 'quality';
export type AiStudioTone = 'friendly' | 'polite' | 'enthusiastic' | 'concise';
export type AiStudioDialect = 'neutral' | 'north' | 'central' | 'south';
export type AiStudioTab = 'overview' | 'profile' | 'knowledge' | 'rules' | 'playground' | 'improve' | 'advanced';

export const AI_STUDIO_ON = 1;
export const AI_STUDIO_OFF = 2;

export interface IAiStudioProfile {
  shopName: string;
  pronoun: string;
  customerPronoun: string;
  tone: AiStudioTone;
  dialect: AiStudioDialect;
  language: 'vi' | 'auto';
  industryId: string;
  intro: string;
  policy: string;
  forbidden: string;
  extra: string;
  greeting: string;
}

export interface IAiStudioPresets {
  certainty: AiStudioCertainty;
  length: AiStudioLength;
}

export interface IAiStudioHourDay {
  day: number; // 1 = Chủ nhật ... 7 = Thứ 7 (giống khung giờ chatbot)
  active: number;
  ranges: { from: string; to: string }[];
}

export interface IAiStudioRules {
  conversationTypes: ('inbox' | 'group')[];
  excludeTagIds: string[];
  hours: { type: AiStudioHoursType; ranges: IAiStudioHourDay[] };
  handoff: {
    askHuman: number;
    topics: string;
    tagIds: string[];
    message: string;
    pauseHours: number;
  };
  fallback: { mode: AiStudioFallbackMode; message: string };
  introduceAsAi: number;
  sellOutOfStock: number;
  rolloutPercent: number;
}

export interface IAiStudioSettings extends AssistantSettings {
  /** Cũ, BE vẫn trả: 'auto' khi reply, còn lại 'suggest'. FE dùng reply, suggest. */
  mode: AiStudioMode;
  /** AI tự trả lời khách (theo quy tắc) */
  reply: boolean;
  /** Gợi ý trả lời cho nhân viên trong hội thoại (kèm tóm tắt) */
  suggest: boolean;
  /** v2 = AI Core (mọi trợ lý mới); '' = cách tìm cũ của trợ lý cũ, chỉ chuyển một chiều sang v2 */
  retrieval: '' | 'v2';
  profile: IAiStudioProfile | null;
  presets: IAiStudioPresets;
  rules: IAiStudioRules | null;
}

/** Bản đang sửa của trợ lý (lưu bằng /chatbot/ai/createassistant) */
export interface IAiStudioDraft {
  id?: string;
  name: string;
  accountId: string;
  platform: AiPlatform | 0;
  model: string;
  knowledgeId: string[];
  status: number;
  settings: IAiStudioSettings;
  /** Hướng dẫn tự viết của trợ lý cũ (chưa có hồ sơ); trợ lý có hồ sơ thì BE tự ghép, không gửi */
  prompt?: string;
}

export const AI_STUDIO_DEFAULT_PROFILE: IAiStudioProfile = {
  shopName: '',
  pronoun: 'shop',
  customerPronoun: 'bạn',
  tone: 'friendly',
  dialect: 'neutral',
  language: 'vi',
  industryId: '',
  intro: '',
  policy: '',
  forbidden: '',
  extra: '',
  greeting: '',
};

export const AI_STUDIO_DEFAULT_RULES: IAiStudioRules = {
  conversationTypes: ['inbox'],
  excludeTagIds: [],
  hours: { type: 'always', ranges: [] },
  handoff: {
    askHuman: AI_STUDIO_ON,
    topics: '',
    tagIds: [],
    message: 'Dạ, shop chuyển nhân viên hỗ trợ mình ngay ạ. Mình chờ shop một chút nhé!',
    pauseHours: 24,
  },
  fallback: {
    mode: 'template_handoff',
    message: 'Dạ câu này shop cần kiểm tra thêm, nhân viên shop sẽ phản hồi mình ngay ạ!',
  },
  introduceAsAi: AI_STUDIO_OFF,
  sellOutOfStock: AI_STUDIO_OFF,
  rolloutPercent: 100,
};

export const AI_STUDIO_DEFAULT_SETTINGS: IAiStudioSettings = {
  temperature: 0.3,
  max_tokens: 500,
  history_message: 10,
  min_score: 1.02,
  top_k: 5,
  typeSendFirst: 1,
  enable_stop_rule: AI_STUDIO_OFF,
  time_collection_message: 0,
  tools: { nhanhvn: { auto_get_info_product: AI_STUDIO_OFF, auto_create_order: AI_STUDIO_OFF } },
  mode: 'auto',
  reply: true,
  suggest: true,
  // Trợ lý mới luôn tìm kiến thức bằng AI Core (BE cũng ép v2 khi tạo); '' chỉ còn ở trợ lý cũ, chuyển một chiều
  retrieval: 'v2',
  profile: null,
  presets: { certainty: 'balanced', length: 'standard' },
  rules: null,
};

/** Trợ lý làm gì: tự trả lời khách, gợi ý cho nhân viên, hoặc cả hai (để hiện trạng thái) */
export type AiStudioRunMode = 'both' | 'reply' | 'suggest';

/** reply, suggest của trợ lý; bản cũ chỉ có mode (suggest = chỉ gợi ý, auto = tự trả lời + gợi ý) */
export function aiStudioWork(settings: Partial<IAiStudioSettings> | null | undefined): { reply: boolean; suggest: boolean } {
  const legacySuggestOnly = settings?.mode === 'suggest';
  const reply = typeof settings?.reply === 'boolean' ? settings.reply : !legacySuggestOnly;
  const suggest = typeof settings?.suggest === 'boolean' ? settings.suggest : true;
  return reply || suggest ? { reply, suggest } : { reply: false, suggest: true };
}

export function aiStudioRunMode(settings: Partial<IAiStudioSettings> | null | undefined): AiStudioRunMode {
  const work = aiStudioWork(settings);
  return work.reply && work.suggest ? 'both' : work.reply ? 'reply' : 'suggest';
}

/** Mức model theo nền tảng (giá tương đối trên key của shop); model cụ thể chọn ở tab Nâng cao */
export const AI_STUDIO_TIERS: Readonly<Record<AiPlatform, Partial<Record<AiStudioTier, string>>>> = {
  [AiPlatform.OpenAI]: { economy: 'gpt-5.6-luna', balanced: 'gpt-5.6-terra', quality: 'gpt-5.6-sol' },
  [AiPlatform.Claude]: { economy: 'claude-haiku-5-5', balanced: 'claude-sonnet-5-5', quality: 'claude-opus-5-5' },
  [AiPlatform.DeepSeek]: { economy: 'deepseek-flash', quality: 'deepseek-v4-pro' },
  [AiPlatform.Gemini]: { economy: 'gemini-3.5-flash-lite', balanced: 'gemini-3.7-flash', quality: 'gemini-3.1-pro-preview' },
  [AiPlatform.Grok]: { economy: 'grok-4.3', balanced: 'grok-4.5', quality: 'grok-4.6' },
};

export const AI_STUDIO_TIER_ORDER: AiStudioTier[] = ['economy', 'balanced', 'quality'];

/** Tên ngắn của nhà cung cấp AI cho người dùng (không kèm tên model) */
const AI_STUDIO_PROVIDER: Readonly<Record<AiPlatform, string>> = {
  [AiPlatform.OpenAI]: 'ChatGPT',
  [AiPlatform.Claude]: 'Claude',
  [AiPlatform.DeepSeek]: 'DeepSeek',
  [AiPlatform.Gemini]: 'Gemini',
  [AiPlatform.Grok]: 'Grok',
};

const AI_STUDIO_MODEL_PREFIX: ReadonlyArray<[RegExp, AiPlatform]> = [
  [/^(gpt|o\d|chatgpt)/i, AiPlatform.OpenAI],
  [/^claude/i, AiPlatform.Claude],
  [/^deepseek/i, AiPlatform.DeepSeek],
  [/^gemini/i, AiPlatform.Gemini],
  [/^grok/i, AiPlatform.Grok],
];

export interface IAiStudioModelInfo {
  platform: AiPlatform | 0;
  /** Tên nhà cung cấp (ChatGPT, Claude, DeepSeek...) */
  provider: string;
  /** Mức của model (khớp đúng mức, không thì đoán theo tên model); null = không xác định */
  tier: AiStudioTier | null;
  /** Đúng một trong các mức của nền tảng (không phải model chọn riêng ở Nâng cao, model cũ) */
  exactTier: boolean;
  /** Tên model đầy đủ (chỉ để hiện trong tooltip) */
  label: string;
}

/**
 * Model hiện cho người dùng là "nhà cung cấp · mức" (VD DeepSeek · Tiết kiệm), tên model đầy đủ chỉ để trong tooltip.
 * Thiếu nền tảng thì đoán theo tên model; model ngoài các mức thì đoán mức gần nhất theo tên (flash, mini... = tiết kiệm).
 */
export function aiStudioModelInfo(model: string | null | undefined, platform?: AiPlatform | number | null): IAiStudioModelInfo {
  const value = String(model ?? '').trim();
  let id = (Number(platform) || 0) as AiPlatform | 0;
  if (!id && value) id = AI_STUDIO_MODEL_PREFIX.find(([pattern]) => pattern.test(value))?.[1] ?? 0;
  const tiers = id ? AI_STUDIO_TIERS[id] ?? {} : {};
  const exact = AI_STUDIO_TIER_ORDER.find(tier => tiers[tier] === value) ?? null;
  let tier = exact;
  if (!tier && value) {
    const name = value.toLowerCase();
    if (/(lite|mini|nano|flash|haiku|luna|small|economy)/.test(name)) tier = 'economy';
    else if (/(pro|opus|sol\b|ultra|large|max)/.test(name)) tier = 'quality';
    else if (/(sonnet|terra|medium|plus)/.test(name)) tier = 'balanced';
  }
  if (tier && id && !tiers[tier]) {
    // Nền tảng không có mức này (VD DeepSeek không có Cân bằng): lấy mức gần nhất có
    tier = tier === 'balanced' ? (tiers.economy ? 'economy' : tiers.quality ? 'quality' : null) : tier;
  }
  const models = id ? CHATBOT_AI_ASSISTANT_MODELS[id] ?? [] : [];
  return {
    platform: id,
    provider: id ? AI_STUDIO_PROVIDER[id] : '',
    tier,
    exactTier: !!exact,
    label: models.find(item => item.value === value)?.label ?? value,
  };
}

export interface IAiStudioPage {
  id: string;
  name: string;
  avatar: string;
  channel: number;
  hasBot: boolean;
  expiredTime: number;
  assistantId: string | null;
  assistantName: string;
  scriptAssistantIds: string[];
}

export interface IAiStudioPagesData {
  pages: IAiStudioPage[];
  pageIds?: string[];
  /** Trợ lý tự bật cho page mới thêm vào doanh nghiệp ('' = chưa bật) */
  newPagesAssistantId?: string;
}

export interface IAiStudioBusinessProfile {
  name: string;
  phone: string;
  email: string;
  addresses: string[];
}

export interface IAiStudioOverviewDay {
  date: string;
  requests: number;
  tokens: number;
  replies: number;
  handoffs: number;
}

export interface IAiStudioQuestionGroup {
  key: string;
  count: number;
  question: string;
  reason: string;
  conversationId: string;
  pageId: string;
  lastAt: number;
}

export interface IAiStudioOverview {
  days: IAiStudioOverviewDay[];
  totals: { requests: number; tokens: number; replies: number; handoffs: number };
  openQuestions: number;
  topQuestions: IAiStudioQuestionGroup[];
}

export interface IAiStudioSource {
  type: string;
  sourceId: string;
  title: string;
  text: string;
  score: number | null;
}

export interface IAiStudioKnowledgeMeta {
  engine: string;
  confidence: number | null;
  sources: IAiStudioSource[];
}

export interface IAiStudioStop {
  reason: string;
  handoff?: boolean;
}

export interface IAiStudioTestAccountData {
  valid: boolean;
  message: string;
}

export interface IAiStudioResolveParams {
  assistantId: string;
  key: string;
  action: 'answer' | 'ignore';
  question?: string;
  answer?: string;
  knowledgeId?: string;
}
